// Maps the QR generator's local form-shape (AnonymousPayload + the
// generator-side design knobs) to the wire-shape consumed by
// `POST /v1/codes`. Pure — same input → same `SaveInputResult`. Tested
// against the eight payload variants and the frame-vocabulary skew
// between the two layers.
//
// Phase 2 of ADR-023 introduces the save path: the generator already
// owns the form state, this mapper is the seam between
// "anonymous render contract" and "create-code contract".

import type {
  CreateCodeDesign,
  CreateCodeOptions,
  CreateCodePayload,
  QrEcc,
  QrFormat,
  QrFrame,
  QrMode,
} from "@qrody/api-client/codes";
import type {
  AnonymousFormat,
  AnonymousFrame,
  AnonymousEcc,
  AnonymousPayload,
} from "@qrody/api-client/render";

export interface SaveDesignInput {
  foreground: string;
  background: string;
  frame: AnonymousFrame;
  /** Optional ECC level — falls through to backend default MEDIUM if absent. */
  ecc?: AnonymousEcc;
  /** Optional logo asset id — Phase 2 leaves this `null` from the
   *  generator since logo upload arrives later (Phase 3+). */
  logoAssetId?: string | null;
  /** Optional QR-spec version floor (1–40). null = encoder auto-pick. */
  minVersion?: number | null;
  /** Editor preference — default download format. Mapped to the backend
   *  upper-case enum on save. */
  format?: AnonymousFormat;
  /** Editor preference — default raster pixel side-length. */
  size?: number;
}

export interface SaveInputArgs {
  payload: AnonymousPayload;
  design: SaveDesignInput;
  /** Optional explicit override; otherwise mode follows the
   *  payload kind (DYNAMIC for url/file, STATIC for the rest). */
  forceMode?: QrMode;
}

export interface SaveInputValue {
  payload: CreateCodePayload;
  design: CreateCodeDesign;
  options: CreateCodeOptions;
}

export type SaveInputResult =
  | { ok: true; value: SaveInputValue }
  | { ok: false; reason: "dynamic-incompatible" };

const DYNAMIC_COMPATIBLE = new Set<AnonymousPayload["type"]>(["url", "file"]);

// Identity mapping today — the front-end `AnonymousFrame` vocabulary
// and the backend `QrFrame` enum are structurally identical (NONE,
// SIMPLE, ROUNDED, SHIELD, CALL_TO_ACTION). The map stays as an
// explicit seam so a future divergence (e.g. when the backend grows a
// distinct BADGE variant) is a one-line fix here rather than a hunt
// through every call-site.
const FRAME_MAP: Record<AnonymousFrame, QrFrame> = {
  NONE: "NONE",
  SIMPLE: "SIMPLE",
  ROUNDED: "ROUNDED",
  SHIELD: "SHIELD",
  CALL_TO_ACTION: "CALL_TO_ACTION",
};

const FORMAT_MAP: Record<AnonymousFormat, QrFormat> = {
  png: "PNG",
  svg: "SVG",
  pdf: "PDF",
};

const trimmedOrUndef = (v: string | undefined): string | undefined => {
  if (v === undefined) return undefined;
  const t = v.trim();
  return t.length === 0 ? undefined : t;
};

function toCreateCodePayload(p: AnonymousPayload): CreateCodePayload {
  switch (p.type) {
    case "url":
      return { kind: "url", url: p.url };
    case "text":
      return { kind: "text", text: p.text };
    case "email": {
      const subject = trimmedOrUndef(p.subject);
      const body = trimmedOrUndef(p.body);
      return {
        kind: "email",
        to: p.to,
        ...(subject !== undefined ? { subject } : {}),
        ...(body !== undefined ? { body } : {}),
      };
    }
    case "sms": {
      const message = trimmedOrUndef(p.message);
      return {
        kind: "sms",
        to: p.to,
        ...(message !== undefined ? { message } : {}),
      };
    }
    case "geo":
      return { kind: "geo", latitude: p.latitude, longitude: p.longitude };
    case "wifi": {
      const password = trimmedOrUndef(p.password);
      return {
        kind: "wifi",
        ssid: p.ssid,
        ...(password !== undefined ? { password } : {}),
        authentication: p.authentication,
        hidden: p.hidden,
      };
    }
    case "vcard": {
      const email = trimmedOrUndef(p.email);
      const phone = trimmedOrUndef(p.phone);
      const org = trimmedOrUndef(p.org);
      const title = trimmedOrUndef(p.title);
      const url = trimmedOrUndef(p.url);
      return {
        kind: "vcard",
        fullName: p.fullName,
        ...(email !== undefined ? { email } : {}),
        ...(phone !== undefined ? { phone } : {}),
        ...(org !== undefined ? { org } : {}),
        ...(title !== undefined ? { title } : {}),
        ...(url !== undefined ? { url } : {}),
      };
    }
    case "file":
      return { kind: "file", downloadUrl: p.downloadUrl };
  }
}

export function buildSaveInput(args: SaveInputArgs): SaveInputResult {
  const dynamicCompatible = DYNAMIC_COMPATIBLE.has(args.payload.type);

  if (args.forceMode === "DYNAMIC" && !dynamicCompatible) {
    return { ok: false, reason: "dynamic-incompatible" };
  }

  const mode: QrMode =
    args.forceMode ?? (dynamicCompatible ? "DYNAMIC" : "STATIC");

  const value: SaveInputValue = {
    payload: toCreateCodePayload(args.payload),
    design: {
      foreground: args.design.foreground,
      background: args.design.background,
      frame: FRAME_MAP[args.design.frame],
      logoAssetId: args.design.logoAssetId ?? null,
      ...(args.design.ecc ? { ecc: args.design.ecc as QrEcc } : {}),
      ...(args.design.minVersion != null ? { minVersion: args.design.minVersion } : {}),
      ...(args.design.format ? { format: FORMAT_MAP[args.design.format] } : {}),
      ...(args.design.size != null ? { size: args.design.size } : {}),
    },
    options: { mode },
  };

  return { ok: true, value };
}
